<template>
  <div class="qr-demo-page">
    <div class="demo-header">
      <h1>二维码弹窗演示</h1>
      <p>点击下方按钮体验邀请二维码功能</p>
    </div>

    <div class="demo-content">
      <div class="demo-section">
        <h3>功能特性</h3>
        <ul class="feature-list">
          <li>✨ 根据URL自动生成二维码海报</li>
          <li>🎨 严格按照设计稿实现完整海报</li>
          <li>🖼️ 生成包含二维码的完整海报图片</li>
          <li>🔄 Canvas绘制，支持转换为base64格式</li>
          <li>📐 响应式设计，适配不同屏幕</li>
          <li>🎯 完整的海报展示体验</li>
        </ul>
      </div>

      <div class="demo-section">
        <h3>组件配置</h3>
        <div class="config-item">
          <label>二维码URL：</label>
          <input 
            v-model="qrUrl" 
            type="url" 
            placeholder="输入要生成二维码的URL"
            class="url-input"
          />
        </div>
        
        <div class="config-item" v-if="currentInviteCode">
          <label>当前邀请码：</label>
          <div class="invite-code-display">{{ currentInviteCode }}</div>
        </div>
      </div>

      <div class="demo-actions">
        <button 
          @click="showQRPopup" 
          class="demo-btn primary"
        >
          生成邀请海报
        </button>
        
        <button 
          @click="resetUrl" 
          class="demo-btn secondary"
        >
          重置URL
        </button>
      </div>

      <div class="demo-section">
        <h3>使用说明</h3>
        <div class="usage-steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              <h4>导入组件</h4>
              <code>import QRCodePopup from '@/components/QRCodePopup.vue'</code>
            </div>
          </div>
          
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              <h4>使用组件</h4>
              <code>&lt;QRCodePopup v-model="visible" :url="qrUrl" @close="handleClose" /&gt;</code>
            </div>
          </div>
          
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              <h4>海报展示</h4>
              <p>组件会生成完整的邀请海报，以base64格式在img标签中展示</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 二维码弹窗组件 -->
    <QRCodePopup 
      :visible="qrPopupVisible"
      :url="qrUrl"
      @close="handleQRPopupClose"
    />
  </div>
</template>

<script>
import QRCodePopup from '@/components/QRCodePopup.vue';

export default {
  name: 'QRCodeDemo',
  components: {
    QRCodePopup,
  },
  data() {
    return {
      qrPopupVisible: false,
      qrUrl: 'https://fingpt.ainvestcn.com/?iv=demo123',
      currentInviteCode: '',
    };
  },
  async mounted() {
    // 获取当前保存的邀请码
    await this.loadInviteCode();
  },
  methods: {
    async loadInviteCode() {
      try {
        const { getValidInviteCode } = await import('@/utils/inviteCode');
        this.currentInviteCode = getValidInviteCode() || '';
        
        if (this.currentInviteCode) {
          // 如果有邀请码，自动构建带邀请码的URL
          this.updateUrlWithInviteCode();
        }
      } catch (error) {
        console.error('加载邀请码时发生错误:', error);
      }
    },
    
    async updateUrlWithInviteCode() {
      if (this.currentInviteCode) {
        try {
          const { buildInviteUrl } = await import('@/utils/inviteCode');
          this.qrUrl = buildInviteUrl('https://fingpt.ainvestcn.com', this.currentInviteCode);
        } catch (error) {
          console.error('构建邀请URL时发生错误:', error);
        }
      }
    },
    
    showQRPopup() {
      this.qrPopupVisible = true;
    },
    
    handleQRPopupClose() {
      this.qrPopupVisible = false;
    },
    
    resetUrl() {
      this.qrUrl = 'https://fingpt.ainvestcn.com';
      this.currentInviteCode = '';
    },
  },
};
</script>

<style lang="less" scoped>
.qr-demo-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.demo-header {
  text-align: center;
  color: white;
  margin-bottom: 40px;
  
  h1 {
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 12px;
  }
  
  p {
    font-size: 16px;
    opacity: 0.9;
  }
}

.demo-content {
  max-width: 800px;
  margin: 0 auto;
}

.demo-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  h3 {
    font-size: 20px;
    color: #333;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #f0f0f0;
  }
}

.feature-list {
  list-style: none;
  padding: 0;
  
  li {
    padding: 8px 0;
    font-size: 16px;
    color: #555;
    
    &:before {
      margin-right: 8px;
    }
  }
}

.config-item {
  display: flex;
  align-items: center;
  gap: 12px;
  
  label {
    font-weight: 500;
    min-width: 100px;
    color: #333;
  }
}

.url-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
}

.invite-code-display {
  flex: 1;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  color: #495057;
  font-family: 'Courier New', monospace;
  font-weight: 600;
}

.demo-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 20px;
}

.demo-btn {
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  
  &.primary {
    background: #667eea;
    color: white;
    
    &:hover {
      background: #5a6fd8;
      transform: translateY(-2px);
    }
  }
  
  &.secondary {
    background: white;
    color: #667eea;
    border: 2px solid #667eea;
    
    &:hover {
      background: #667eea;
      color: white;
      transform: translateY(-2px);
    }
  }
  
  &:active {
    transform: translateY(0);
  }
}

.usage-steps {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.step-number {
  width: 32px;
  height: 32px;
  background: #667eea;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
  
  h4 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 16px;
  }
  
  p {
    margin: 0;
    color: #666;
    line-height: 1.5;
  }
  
  code {
    background: #f5f5f5;
    padding: 4px 8px;
    border-radius: 4px;
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 14px;
    color: #e83e8c;
    display: inline-block;
    word-break: break-all;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .demo-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .demo-btn {
    width: 200px;
  }
  
  .config-item {
    flex-direction: column;
    align-items: flex-start;
    
    label {
      min-width: auto;
    }
  }
  
  .url-input {
    width: 100%;
  }
  
  .step {
    flex-direction: column;
    text-align: center;
  }
  
  .step-content {
    text-align: left;
  }
}
</style> 